import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Shield, Bed, Calendar, DollarSign, Users, Clock, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPropertyView() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPropertyData();
  }, [propertyId]);

  const fetchPropertyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/properties/${propertyId}/full-view`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes acceso activo a esta propiedad');
        navigate('/admin');
      } else {
        toast.error('Error al cargar datos de la propiedad');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return null;

  const { property, owner, rooms, reservations, payments, access_info, stats } = data;
  const expiration = access_info?.fecha_expiracion ? new Date(access_info.fecha_expiracion) : null;

  const roomStatusColor = (estado) => {
    const colors = { disponible: 'bg-green-100 text-green-700', ocupada: 'bg-orange-100 text-orange-700', mantenimiento: 'bg-yellow-100 text-yellow-700', limpieza: 'bg-blue-100 text-blue-700' };
    return colors[estado] || 'bg-gray-100 text-gray-700';
  };

  const reservationStatusColor = (estado) => {
    const colors = { confirmada: 'bg-blue-100 text-blue-700', checked_in: 'bg-green-100 text-green-700', checked_out: 'bg-gray-100 text-gray-700', cancelada: 'bg-red-100 text-red-700' };
    return colors[estado] || 'bg-gray-100 text-gray-700';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-property-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>{property.nombre}</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {property.ciudad} &middot; {property.codigo_unico}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-500 text-white"><Shield className="h-3 w-3 mr-1" /> Modo Soporte</Badge>
            {expiration && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" /> Expira: {expiration.toLocaleString()}
              </Badge>
            )}
          </div>
        </div>

        {/* Owner Info */}
        {owner && (
          <Card className="border-l-4 border-l-amber-400 bg-amber-50/50">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-5 w-5 text-amber-600" />
              <div>
                <span className="font-medium">{owner.nombre}</span>
                <span className="text-sm text-muted-foreground ml-3">{owner.email}</span>
                {owner.telefono && <span className="text-sm text-muted-foreground ml-3">{owner.telefono}</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardContent className="p-4 text-center">
              <Bed className="h-6 w-6 mx-auto mb-1 text-blue-500" />
              <p className="text-2xl font-bold">{stats.total_habitaciones}</p>
              <p className="text-xs text-muted-foreground">Habitaciones</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Bed className="h-6 w-6 mx-auto mb-1 text-orange-500" />
              <p className="text-2xl font-bold">{stats.habitaciones_ocupadas}</p>
              <p className="text-xs text-muted-foreground">Ocupadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 mx-auto mb-1 text-purple-500" />
              <p className="text-2xl font-bold">{stats.total_reservas}</p>
              <p className="text-xs text-muted-foreground">Total Reservas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 mx-auto mb-1 text-green-500" />
              <p className="text-2xl font-bold">{stats.reservas_activas}</p>
              <p className="text-xs text-muted-foreground">Reservas Activas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold">${stats.total_pagos?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Pagos</p>
            </CardContent>
          </Card>
        </div>

        {/* Rooms */}
        <Card>
          <CardHeader><CardTitle style={{fontFamily: 'Manrope'}}>Habitaciones ({rooms.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {rooms.map((room) => (
                <div key={room.id} className={`p-3 rounded-lg border ${roomStatusColor(room.estado)}`}>
                  <p className="font-semibold">{room.nombre || room.numero}</p>
                  <p className="text-xs">{room.tipo} &middot; {room.estado}</p>
                </div>
              ))}
              {rooms.length === 0 && <p className="text-muted-foreground col-span-4">Sin habitaciones registradas</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recent Reservations */}
        <Card>
          <CardHeader><CardTitle style={{fontFamily: 'Manrope'}}>Reservas Recientes ({reservations.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {reservations.map((res) => (
                <div key={res.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-medium">{res.guest_name || 'Sin nombre'}</p>
                    <p className="text-xs text-muted-foreground">
                      {res.start_date} → {res.end_date} &middot; {res.room_name || ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">${res.total_amount?.toLocaleString()}</span>
                    <Badge className={reservationStatusColor(res.estado)} variant="outline">{res.estado}</Badge>
                  </div>
                </div>
              ))}
              {reservations.length === 0 && <p className="text-muted-foreground">Sin reservas</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card>
          <CardHeader><CardTitle style={{fontFamily: 'Manrope'}}>Pagos Recientes ({payments.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {payments.map((pay) => (
                <div key={pay.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{pay.method}</p>
                    <p className="text-xs text-muted-foreground">{pay.created_at ? new Date(pay.created_at).toLocaleString() : ''}</p>
                  </div>
                  <span className="font-bold text-green-600">${pay.amount?.toLocaleString()}</span>
                </div>
              ))}
              {payments.length === 0 && <p className="text-muted-foreground">Sin pagos registrados</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
